import { useDotLegalBreadCrumbs } from "@dotlegal/dotlegal-ui-components";
import { useEffect, useMemo, useState } from "react";
import { userIsReadOnly } from "../../auth/AuthService";
import { HttpRequestError } from "../../auth/HttpInterceptor";
import { useStateLocalStorage } from "../../common/hooks/useStateLocalStorage";
import { CaseType } from "../../core/cases/Case.types";
import { CaseSummarization, CaseSummarizationPage } from "../../core/cases/CaseSummarizationPage";
import { YearlyCosts } from "../../core/costs/Cost.types";
import { getYearlyCosts } from "../../core/costs/CostService";
import {
    FutureEstimatedRenewalCost,
    FutureEstimatedRenewalCostsView,
    futureEstimatedRenewalCostsViewStorageKey,
    RegistrationsPerClass,
    WidgetConfig,
    YearlyRenewalsAndApplications,
} from "../../core/dashboard/Dashboard.types";
import DashboardGrid from "../../core/dashboard/DashboardGrid";
import { getFutureEstimatedRenewalCosts, getRegistrationsPerClass, getYearlyRenewalsAndApplications } from "../../core/dashboard/DashboardService";
import {
    generateCostWidget,
    generateFutureEstimatedRenewalCostsWidget,
    generateRegistrationsPerClassWidget,
    generateRenewalsAndApplicationsWidget,
    generateUpcomingRenewalsWidget,
} from "../../core/dashboard/DashboardWidgetService";
import { Currency } from "../../core/organizations/Organization.types";
import { CaseSummarizationProps } from "../../Main";

export function Dashboard(props: CaseSummarizationProps) {
    useDotLegalBreadCrumbs([{ name: "Dashboard" }]);
    const editSetRenewalStatus = useMemo(() => {
        return props.userSession.verifiedUser?.userRoles.some((value) => value.name === "Employee" || value.name === "Admin") ?? false;
    }, [props.userSession.verifiedUser?.userRoles]);
    const [yearlyRenewalsAndApplications, setYearlyRenewalsAndApplications] = useState<WidgetConfig<YearlyRenewalsAndApplications[]>>({
        data: [{ year: new Date().getFullYear(), applications: 0, renewals: 0, total: 0 }],
        isLoading: true,
        serviceUnavailable: false,
        accessDenied: false,
    });
    const [registrationsPerClass, setRegistrationsPerClass] = useState<WidgetConfig<RegistrationsPerClass>>({
        data: {
            totalCaseCount: 0,
            countByTop12Classes: [
                { class: "1", count: 0 },
                { class: "1", count: 0 },
                { class: "1", count: 0 },
                { class: "1", count: 0 },
                { class: "1", count: 0 },
                { class: "1", count: 0 },
                { class: "1", count: 0 },
                { class: "1", count: 0 },
                { class: "1", count: 0 },
                { class: "1", count: 0 },
                { class: "1", count: 0 },
                { class: "1", count: 0 },
            ],
        },
        isLoading: true,
        serviceUnavailable: false,
        accessDenied: false,
    });
    const [yearlyCosts, setYearlyCosts] = useState<WidgetConfig<YearlyCosts[]>>({
        data: [],
        isLoading: true,
        serviceUnavailable: false,
        accessDenied: false,
    });
    const [futureEstimatedRenewalCosts, setFutureEstimatedRenewalCosts] = useState<WidgetConfig<FutureEstimatedRenewalCost[]>>({
        data: [],
        isLoading: true,
        serviceUnavailable: false,
        accessDenied: false,
    });
    const [futureEstimatedRenewalCostsView, setFutureEstimatedRenewalCostsView] = useStateLocalStorage<FutureEstimatedRenewalCostsView>(
        futureEstimatedRenewalCostsViewStorageKey,
        { mode: "year", selectedYear: new Date().getFullYear() }
    );
    const showFutureEstimatedRenewalCosts =
        (props.userSession.selectedOrg?.futureEstimatedRenewalCostsActive ?? false) &&
        (props.userSession.selectedOrg?.hasShowRenewalPriceRight ?? false) &&
        !userIsReadOnly(props.userSession.verifiedUser) &&
        props.selectedCaseType !== CaseType.Copyright;

    const [upcomingRenewals, setUpcomingRenewals] = useState(
        generateUpcomingRenewalsWidget(
            "registered",
            props.selectedCaseType,
            props.renewalRegistrationActive,
            editSetRenewalStatus,
            props.userSession.verifiedUser
        )
    );

    const updateDashboardData = (caseType: CaseType) => {
        if (!props.orgHasBeenSelected) return;
        setYearlyRenewalsAndApplications({
            ...yearlyRenewalsAndApplications,
            isLoading: true,
        });
        getYearlyRenewalsAndApplications(caseType)
            .then((res) =>
                setYearlyRenewalsAndApplications({
                    data: res || [],
                    isLoading: false,
                    serviceUnavailable: false,
                    accessDenied: false,
                })
            )
            .catch((e: HttpRequestError) =>
                setYearlyRenewalsAndApplications({
                    data: [],
                    isLoading: false,
                    serviceUnavailable: e.statusCode !== 403,
                    accessDenied: e.statusCode === 403,
                })
            );
        setRegistrationsPerClass({ ...registrationsPerClass, isLoading: true });
        getRegistrationsPerClass(caseType)
            .then((res) =>
                setRegistrationsPerClass({
                    data: res,
                    isLoading: false,
                    serviceUnavailable: false,
                    accessDenied: false,
                })
            )
            .catch((e: HttpRequestError) =>
                setRegistrationsPerClass({
                    data: { totalCaseCount: 0, countByTop12Classes: [] },
                    isLoading: false,
                    serviceUnavailable: e.statusCode !== 403,
                    accessDenied: e.statusCode === 403,
                })
            );
        if (showFutureEstimatedRenewalCosts) {
            setFutureEstimatedRenewalCosts({ ...futureEstimatedRenewalCosts, isLoading: true });
            getFutureEstimatedRenewalCosts(caseType)
                .then((res) =>
                    setFutureEstimatedRenewalCosts({
                        data: res || [],
                        isLoading: false,
                        serviceUnavailable: false,
                        accessDenied: false,
                    })
                )
                .catch((e: HttpRequestError) =>
                    setFutureEstimatedRenewalCosts({
                        data: [],
                        isLoading: false,
                        serviceUnavailable: e.statusCode !== 403,
                        accessDenied: e.statusCode === 403,
                    })
                );
        }
        setYearlyCosts({ ...yearlyCosts, isLoading: true });
        if (!!props.userCostAccess && props.userCostAccess !== "Hide costs") {
            getYearlyCosts(caseType, false, "")
                .then((res) => {
                    setYearlyCosts({
                        data: res || [],
                        isLoading: false,
                        serviceUnavailable: false,
                        accessDenied: false,
                    });
                })
                .catch((e: HttpRequestError) => {
                    setYearlyCosts({
                        data: [],
                        isLoading: false,
                        serviceUnavailable: e.statusCode !== 403,
                        accessDenied: e.statusCode === 403,
                    });
                });
        }
    };

    useEffect(() => {
        updateDashboardData(props.selectedCaseType);
        // eslint-disable-next-line
    }, [props.selectedCaseType, props.orgHasBeenSelected]);

    useEffect(() => {
        setUpcomingRenewals(
            generateUpcomingRenewalsWidget(
                "registered",
                props.selectedCaseType,
                props.renewalRegistrationActive,
                editSetRenewalStatus,
                props.userSession.verifiedUser
            )
        );
    }, [editSetRenewalStatus, props.renewalRegistrationActive, props.selectedCaseType, props.userSession.verifiedUser]);

    return (
        <CaseSummarizationPage
            onCaseFilterKeyChange={props.onCaseFilterKeyChanged}
            onCaseTypeFilterConfigurationClear={props.onCaseTypeFilterConfigurationCleared}
            caseSummarization={CaseSummarization.Dashboard}
        >
            <DashboardGrid
                widgets={[
                    generateRenewalsAndApplicationsWidget(yearlyRenewalsAndApplications, props.selectedCaseType),
                    props.selectedCaseType === CaseType.Trademark ? generateRegistrationsPerClassWidget(registrationsPerClass) : null,
                    !!props.userCostAccess && props.userCostAccess !== "Hide costs" && !userIsReadOnly(props.userSession.verifiedUser)
                        ? generateCostWidget(yearlyCosts, props.selectedCaseType)
                        : null,
                    upcomingRenewals,
                    showFutureEstimatedRenewalCosts
                        ? generateFutureEstimatedRenewalCostsWidget(
                              futureEstimatedRenewalCosts,
                              props.userSession.selectedOrg?.currency ?? Currency.DKK,
                              futureEstimatedRenewalCostsView,
                              setFutureEstimatedRenewalCostsView
                          )
                        : null,
                ]}
            />
        </CaseSummarizationPage>
    );
}

export default Dashboard;
